/**
 * useAppColorMode — global Dark/Light preference for the entire application.
 */

import { useSyncExternalStore, useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  APP_COLOR_MODE_KEY,
  COLOR_MODE,
  COLOR_MODE_EVENT,
  ensureSessionColorMode,
  peekColorMode,
  readPersistedMode,
  syncDocumentColorMode,
  writePersistedMode,
} from 'theme/appColorMode';

function subscribe(onChange) {
  const handleStorage = (event) => {
    if (
      event.key === APP_COLOR_MODE_KEY ||
      event.key === 'decisionPoint.colorMode' ||
      event.key === 'voxpont.colorMode'
    ) {
      onChange();
    }
  };
  window.addEventListener(COLOR_MODE_EVENT, onChange);
  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener(COLOR_MODE_EVENT, onChange);
    window.removeEventListener('storage', handleStorage);
  };
}

/**
 * @returns {{
 *   mode: 'dark' | 'light',
 *   resolvedMode: 'dark' | 'light',
 *   isDark: boolean,
 *   hasExplicitPreference: boolean,
 *   setMode: (m: string) => void,
 *   toggle: () => void,
 * }}
 */
export function useAppColorMode() {
  const { pathname } = useLocation();

  const resolvedMode = useSyncExternalStore(
    subscribe,
    () => peekColorMode(pathname),
    () => peekColorMode(pathname)
  );

  const hasExplicitPreference = useMemo(() => Boolean(readPersistedMode()), [resolvedMode]);

  const setMode = useCallback((next) => {
    writePersistedMode(next);
  }, []);

  const toggle = useCallback(() => {
    const current = peekColorMode(window.location.pathname);
    writePersistedMode(
      current === COLOR_MODE.DARK ? COLOR_MODE.LIGHT : COLOR_MODE.DARK
    );
  }, []);

  useEffect(() => {
    ensureSessionColorMode(pathname);
    syncDocumentColorMode(peekColorMode(pathname));
  }, [pathname]);

  return useMemo(
    () => ({
      mode: resolvedMode,
      resolvedMode,
      isDark: resolvedMode === COLOR_MODE.DARK,
      hasExplicitPreference,
      setMode,
      toggle,
    }),
    [resolvedMode, hasExplicitPreference, setMode, toggle]
  );
}

export default useAppColorMode;
